import { Address, Asset, Collection, Vault } from 'nftx.js';
import AssetImage from '../../elements/AssetImage';
import CollectionImage from '../../elements/CollectionImage';
import Collapsible from '../../modules/Collapsible';
import Table from '../../modules/Table';
import Price from '../../elements/Price';
import ellipsis from '@/presentation/utils/ellipsis';
import Button from '../../elements/Button';
import ChoiceButton from '../../elements/ChoiceButton';
import { MutableRefObject } from 'react';

// const AssetVault;
const Asset = ({
  assetAddress,
  tokenId,
  quantity,
  name,
  collection,
  rarity,
  rarityRank,
  traits,
  onSwapClick,
  onDepositClick,
  userOwnsAsset,
  vaultId,
  vaultIds,
  vaults,
  root,
}: {
  assetAddress: Address;
  tokenId: string;
  quantity: bigint;
  collection: Collection;
  userOwnsAsset: boolean;
  vaultId?: string;
  vaults?: Vault[];
  onSwapClick: (vaultId: string) => void;
  onDepositClick: (vaultId: string) => void;
  root?: MutableRefObject<any>;
} & Partial<Asset>) => {
  const isPurchasable =
    vaultId != null &&
    !!vaults?.find((v) => v.vaultId === vaultId)?.prices[0].redeem.price;

  const vault = vaults?.find((v) => v.vaultId === vaultId);

  const rarityPercentage =
    rarityRank && collection.tokenCount
      ? rarityRank / collection.tokenCount
      : 1;

  return (
    <div className="grid grid-cols-3 gap-8">
      <div className="col-span-1 space-y-4">
        <AssetImage
          assetAddress={assetAddress}
          tokenId={tokenId}
          className="rounded-lg"
        />
        <hgroup>
          <div className="flex items-center">
            <CollectionImage
              collection={assetAddress}
              className="h-6 w-6 rounded mr-2"
            />
            <h1 className="font-medium text-gray-500">{collection.name}</h1>
          </div>
          <div className="flex items-center">
            <h2 className="font-bold text-xl">{`${name}`}</h2>
            {!!rarity && (
              <h4 className="space-x-4 ml-auto pl-4">
                <span
                  className={`ml-auto p-1.5 flex rounded-md items-center text-sm ${
                    rarityPercentage < 0.25
                      ? 'bg-gray-50 border border-pink-300'
                      : 'bg-gray-200'
                  }`}
                >
                  <span className="inline-flex items-center">
                    <span className="text-gray-500 w-5 h-5 mr-1">
                      <svg
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill="currentColor"
                          d="M18.9 7.2l-3-4c-0.077-0.103-0.191-0.172-0.318-0.193l-6-1c-0.054-0.009-0.11-0.009-0.164 0l-6 1c-0.127 0.021-0.241 0.090-0.318 0.193l-3 4c-0.143 0.191-0.131 0.457 0.028 0.634l9 10c0.095 0.105 0.23 0.166 0.372 0.166s0.277-0.060 0.372-0.166l9-10c0.16-0.178 0.172-0.443 0.028-0.634zM12.786 7l-3.286 9.037-3.286-9.037h6.572zM6.707 6l2.793-2.793 2.793 2.793h-5.586zM10.957 3.25l3.698 0.616-1.233 1.849-2.466-2.465zM5.578 5.715l-1.233-1.849 3.698-0.616-2.465 2.465zM4.67 6.156l-2.985 0.597 1.791-2.388 1.194 1.791zM5.177 7.075l2.974 8.179-6.692-7.436 3.718-0.744zM13.823 7.075l3.718 0.744-6.692 7.436 2.974-8.179zM14.33 6.156l1.194-1.791 1.791 2.388-2.985-0.597z"
                        ></path>
                      </svg>
                    </span>
                    <span
                      className={
                        rarityPercentage < 0.5
                          ? 'bg-gradient-to-r bg-clip-text text-transparent from-orange-500 to-pink-500 font-bold'
                          : 'text-gray-600'
                      }
                    >
                      {rarityRank}
                    </span>
                  </span>
                </span>
              </h4>
            )}
          </div>
        </hgroup>
        <dl className="space-y-1">
          <div>
            <dt className="font-medium text-gray-500">{'Address'}</dt>
            <dd className="text-gray-800 font-medium">
              {ellipsis(collection.address)}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-gray-500">{'Floor Price'}</dt>
            <dd className="text-gray-800 font-medium">
              <Price value={collection.floorPrice} />
            </dd>
          </div>
          <div>
            <dt className="font-medium text-gray-500">{'Standard'}</dt>
            <dd className="text-gray-800 font-medium">{collection.standard}</dd>
          </div>
        </dl>
      </div>
      <div className="col-span-2 space-y-4">
        {vault && (
          <dl>
            <dt className="font-medium text-base text-gray-500">{'Price'}</dt>
            <dd className="font-bold text-gray-800 text-2xl">
              {isPurchasable ? (
                <Price value={vault.prices[0].redeem.price} />
              ) : (
                '-'
              )}
            </dd>
          </dl>
        )}
        {userOwnsAsset && (
          <Table
            headings={[
              {
                label: 'Vault',
                align: 'left',
              },
              {
                label: 'Sell Price',
              },
              {
                label: 'APR',
              },
              {
                label: 'Action',
              },
            ]}
            rows={vaultIds?.map((vId) => {
              const vault = vaults?.find((v) => v.vaultId === vId);
              if (!vault) return [];
              return [
                <div key="name">
                  {vault.token.name}
                  <br />
                  <span className="uppercase text-gray-500 text-sm">
                    {vault.token.symbol}
                  </span>
                </div>,
                <Price value={vault.prices[0].mint.price} key="price" />,
                // @TODO jackieboi APR?
                <span key="apr">{'-'}</span>,
                <div className="flex items-center justify-end" key="action">
                  <ChoiceButton
                    size="small"
                    root={root}
                    options={[
                      {
                        label: 'Deposit',
                        onClick: () => onDepositClick(vId),
                      },
                      {
                        label: 'Sell',
                        onClick: () => onSwapClick(vId),
                      },
                    ]}
                  >
                    {'Deposit'}
                  </ChoiceButton>
                </div>,
              ];
            })}
          />
        )}
        {isPurchasable && (
          <Button
            onClick={() => onSwapClick(vaultId)}
            size="large"
            className="w-full"
          >
            {'Buy'}
          </Button>
        )}
        <Collapsible
          open={true}
          header={<h3 className="text-base font-bold">{'Traits'}</h3>}
        >
          <Table
            border={false}
            headings={[
              {
                label: 'Name',
                align: 'left',
                className: 'bg-gray-50',
              },
              {
                label: 'Value',
                className: 'bg-gray-50',
              },
            ]}
            rows={Object.entries(traits || {}).map(([key, val]) => [
              key,
              <span className="text-sm font-medium text-gray-800" key="value">
                {val}
              </span>,
            ])}
          />
        </Collapsible>
      </div>
    </div>
  );
};

export default Asset;
